import {
  isLegacyLog,
  logOccurrenceKey,
  occurrencesInPersonalDay,
  personalDateOfKey,
  readCheckinSchedule,
  splitLocalDateTime,
  isTimedKey,
  type LocalDateTime,
  type OccurrenceItemData,
  type OccurrenceKey,
} from './checkin-occurrence';

/**
 * The check-in occurrences of one personal day (data dictionary §3.2): the single view model the
 * card, the edit mode, the print sheet and the reminders share. An item can occur twice on a day
 * (a planned time moved across the day start) or not at all.
 */

/** Why an occurrence is on the day's list. */
export const OccurrenceReason = {
  /** The rule and the plan make it due on the day. */
  Scheduled: 'scheduled',
  /** Not due on the day, but done on it (an extra tick, a disabled item, a changed plan). */
  DoneOnly: 'doneOnly',
} as const;
export type OccurrenceReason = (typeof OccurrenceReason)[keyof typeof OccurrenceReason];

/** What the day list reads from a check-in item record. */
export interface CheckinDayItem {
  readonly id: string;
  readonly data: OccurrenceItemData & {
    readonly name: string;
    readonly sort: number;
  };
}

/** What the day list reads from a check-in log record. */
export interface CheckinDayLog {
  readonly itemId: string;
  readonly personalDate: string;
  readonly occursAt?: unknown;
  readonly date?: unknown;
  readonly done: boolean;
  readonly doneAt: string | null;
  /** Envelope HLC: between a legacy and a new record of one occurrence the newer wins. */
  readonly hlc?: string;
}

export interface CheckinOccurrence<T extends CheckinDayItem = CheckinDayItem> {
  /** Unique within the day: `{itemId}|{occurrenceKey}`. */
  readonly key: string;
  readonly item: T;
  readonly itemId: string;
  /** The occurrence key (planned local date-time, or the date when untimed). */
  readonly occurrence: OccurrenceKey;
  readonly occursAt: LocalDateTime | null;
  readonly name: string;
  /** Planned time of day, or null. */
  readonly plannedMin: number | null;
  readonly done: boolean;
  /** Actual completion instant (ISO), or null when not done or not recorded. */
  readonly doneAt: string | null;
  readonly reason: OccurrenceReason;
}

/** Key of an occurrence on a day list. */
export const checkinOccurrenceKey = (itemId: string, occurrence: OccurrenceKey): string =>
  `${itemId}|${occurrence}`;

/** Item and occurrence of a day-list key, or null when it is not one. */
export function parseCheckinOccurrenceKey(
  key: string,
): { readonly itemId: string; readonly occurrence: string } | null {
  const separator = key.indexOf('|');
  if (separator <= 0) return null;
  return { itemId: key.slice(0, separator), occurrence: key.slice(separator + 1) };
}

/** The completion state of each occurrence of each item: the newer of a legacy and a new record. */
export function resolveCheckinLogs<L extends CheckinDayLog>(
  items: readonly { readonly id: string; readonly data: OccurrenceItemData }[],
  logs: readonly L[],
  legacyDayStartMin: number,
): Map<string, L> {
  const versionsOf = new Map(
    items.map((item) => [item.id, readCheckinSchedule(item.data, legacyDayStartMin)]),
  );
  const resolved = new Map<string, L>();
  for (const log of logs) {
    const versions = versionsOf.get(log.itemId);
    if (versions === undefined) continue;
    const key = checkinOccurrenceKey(log.itemId, logOccurrenceKey(log, versions));
    const held = resolved.get(key);
    if (held === undefined || (held.hlc ?? '') < (log.hlc ?? '')) resolved.set(key, log);
  }
  return resolved;
}

/** Order inside a day: by planned local date-time, untimed occurrences last, then `sort`, then id. */
function compareOccurrences(a: CheckinOccurrence, b: CheckinOccurrence): number {
  const timeA = a.occursAt;
  const timeB = b.occursAt;
  if (timeA !== timeB) {
    if (timeA === null) return 1;
    if (timeB === null) return -1;
    return timeA < timeB ? -1 : 1;
  }
  if (a.item.data.sort !== b.item.data.sort) return a.item.data.sort - b.item.data.sort;
  return a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0;
}

/**
 * The occurrences of `personalDate`: every occurrence due on it, plus completions recorded for an
 * occurrence of the day that is no longer due (DoneOnly). `extraKeys` keeps rows listed regardless
 * (the card keeps a row unticked during the page session). Logs must cover the personal days
 * around the date (the caller queries D−2 … D+2: legacy logs carry the rule day).
 */
export function checkinDayOccurrences<T extends CheckinDayItem, L extends CheckinDayLog>(
  items: readonly T[],
  logs: readonly L[],
  personalDate: string,
  dayStartMin: number,
  legacyDayStartMin: number,
  extraKeys: ReadonlySet<string> = new Set(),
): CheckinOccurrence<T>[] {
  const resolved = resolveCheckinLogs(items, logs, legacyDayStartMin);
  const result: CheckinOccurrence<T>[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const versions = readCheckinSchedule(item.data, legacyDayStartMin);
    for (const slot of occurrencesInPersonalDay(item.data, versions, personalDate, dayStartMin)) {
      const key = checkinOccurrenceKey(item.id, slot.key);
      const log = resolved.get(key);
      seen.add(key);
      result.push({
        key,
        item,
        itemId: item.id,
        occurrence: slot.key,
        occursAt: slot.occursAt,
        name: item.data.name,
        plannedMin: slot.plannedMin,
        done: log?.done === true,
        doneAt: log?.done === true ? log.doneAt : null,
        reason: OccurrenceReason.Scheduled,
      });
    }
  }
  const byId = new Map(items.map((item) => [item.id, item]));
  for (const [key, log] of resolved) {
    if (seen.has(key)) continue;
    const parsed = parseCheckinOccurrenceKey(key)!;
    const item = byId.get(parsed.itemId);
    if (item === undefined || personalDateOfKey(parsed.occurrence, dayStartMin) !== personalDate) continue;
    if (!log.done && !extraKeys.has(key)) continue;
    const timed = isTimedKey(parsed.occurrence);
    result.push({
      key,
      item,
      itemId: item.id,
      occurrence: parsed.occurrence,
      occursAt: timed ? parsed.occurrence : null,
      name: item.data.name,
      plannedMin: timed ? splitLocalDateTime(parsed.occurrence).min : null,
      done: log.done,
      doneAt: log.done ? log.doneAt : null,
      reason: OccurrenceReason.DoneOnly,
    });
  }
  return result.sort(compareOccurrences);
}

/** Whether a completion record is located by its personal date only (written before occurrences). */
export const isLegacyCheckinLog = (log: CheckinDayLog): boolean => isLegacyLog(log);
